import Keycloak from 'keycloak-js';

export const PERMISSIONS = {
  dashboardView: 'dashboard:view',
  policiesView: 'policies:view',
  policiesIssue: 'policies:issue',
  ordersView: 'orders:view',
  ordersManage: 'orders:manage',
  ordersCancel: 'orders:cancel',
  ordersDiscount: 'orders:discount',
  clientsView: 'clients:view',
  clientsManage: 'clients:manage',
  clientsFloat: 'clients:float',
  fleetView: 'fleet:view',
  fleetManage: 'fleet:manage',
  borderView: 'border:view',
  borderIssue: 'border:issue',
  comesaRegistryView: 'comesa:registry:view',
  comesaFilesManage: 'comesa:files:manage',
  comesaRatesManage: 'comesa:rates:manage',
  comesaStockManage: 'comesa:stock:manage',
  comesaRefundsEdit: 'comesa:refunds:edit',
  reportsView: 'reports:view',
  reportsExport: 'reports:export',
  reportsAccessManage: 'reports:access:manage',
  usersView: 'users:view',
  usersManage: 'users:manage',
  platformAdmin: 'platform:admin',
  comesaBranchesManage: 'comesa:branches:manage',
} as const;

export const MODULES = {
  comesa: 'module:comesa',
  rawsur: 'module:rawsur',
} as const;

type KeycloakRoleState = Pick<Keycloak, 'authenticated' | 'realmAccess'>;

export function hasPermission(
  keycloak: KeycloakRoleState,
  ...required: readonly string[]
): boolean {
  if (!keycloak.authenticated) return false;
  const held = keycloak.realmAccess?.roles ?? [];
  return required.some(permission => held.includes(permission));
}

export function hasModule(keycloak: KeycloakRoleState, module: string): boolean {
  return hasPermission(keycloak, module, PERMISSIONS.platformAdmin);
}

export function rawsurAllowedPickupMethods(keycloak: KeycloakRoleState): Set<string> {
  const methods = new Set<string>();
  if (hasPermission(keycloak, PERMISSIONS.policiesIssue)) {
    methods.add('ONLINE');
    methods.add('OFFICE');
  }
  if (hasPermission(keycloak, PERMISSIONS.borderIssue)) {
    methods.add('BORDER');
  }
  return methods;
}

export function canManageBranches(keycloak: KeycloakRoleState): boolean {
  return hasPermission(
    keycloak,
    PERMISSIONS.comesaBranchesManage,
    PERMISSIONS.platformAdmin,
  );
}

export function isUserRole(
  keycloak: any,
  userRole?: string | null,
  ...targetRoles: readonly string[]
): boolean {
  if (userRole && targetRoles.includes(userRole)) {
    return true;
  }
  if (!keycloak) return false;

  const realmRoles: string[] = keycloak.realmAccess?.roles ?? [];
  if (targetRoles.some(r => realmRoles.includes(r))) {
    return true;
  }

  const tokenRealmRoles: string[] = keycloak.tokenParsed?.realm_access?.roles ?? [];
  if (targetRoles.some(r => tokenRealmRoles.includes(r))) {
    return true;
  }

  const resourceRoles: string[] = Object.values(keycloak.resourceAccess ?? {})
    .flatMap((res: any) => res?.roles ?? []);
  if (targetRoles.some(r => resourceRoles.includes(r))) {
    return true;
  }

  const tokenResourceRoles: string[] = Object.values(keycloak.tokenParsed?.resource_access ?? {})
    .flatMap((res: any) => res?.roles ?? []);
  if (targetRoles.some(r => tokenResourceRoles.includes(r))) {
    return true;
  }

  const tokenRole = keycloak.tokenParsed?.['role'] || keycloak.tokenParsed?.['job_role'];
  if (tokenRole && targetRoles.includes(tokenRole)) {
    return true;
  }

  const tokenRolesList = keycloak.tokenParsed?.['roles'];
  if (Array.isArray(tokenRolesList) && targetRoles.some(r => tokenRolesList.includes(r))) {
    return true;
  }

  if (typeof keycloak.hasRealmRole === 'function') {
    if (targetRoles.some(r => keycloak.hasRealmRole(r))) return true;
  }

  return false;
}

export function canOverrideRates(keycloak: any, userRole?: string | null): boolean {
  return isUserRole(
    keycloak,
    userRole,
    'platform_admin',
    'broker_admin',
    PERMISSIONS.platformAdmin,
  );
}

export function canChangeRates(keycloak: any, userRole?: string | null): boolean {
  if (canOverrideRates(keycloak, userRole)) return true;
  if (isUserRole(keycloak, userRole, 'broker_super')) return true;
  return hasPermission(keycloak, PERMISSIONS.comesaRatesManage);
}


